import { getField, updateField } from 'vuex-map-fields'
import { Api } from '@/plugins/axios'
import { errorHandler } from '@/plugins/errorHandler'

const defaultStats = {
  totalLeads: 0,
  completedCalls: 0,
  successfulTransfers: 0,
  appointmentsSet: 0,
  messagesCollected: 0,
  noAnswers: 0,
}

export const state = () => ({
  activeCampaign: null,
  campaignStatus: null,
  liveStatus: null,
  transcript: [],
  callHistory: [],
  loading: false,
  polling: null,
  wsUrl: null,
  wsToken: null,
})

export const getters = {
  getField,
  isActive: (state) => {
    return state.liveStatus && ['calling', 'paused'].includes(state.liveStatus.state)
  },
  currentCall: (state) => {
    return state.liveStatus?.currentCall || null
  },
  stats: (state) => {
    return state.liveStatus?.stats || state.campaignStatus?.campaign?.stats || { ...defaultStats }
  },
  queueDepth: (state) => {
    return state.liveStatus?.queueDepth || 0
  },
  campaignState: (state) => {
    return state.liveStatus?.state || state.activeCampaign?.status || 'created'
  },
}

export const mutations = {
  updateField,

  setCampaign(state, campaign) {
    state.activeCampaign = campaign
  },

  setCampaignStatus(state, { campaign, live }) {
    state.campaignStatus = { campaign }
    state.liveStatus = live
  },

  setTranscriptWsCredentials(state, { url, token }) {
    state.wsUrl = url || null
    state.wsToken = token || null
  },

  setLiveStatus(state, live) {
    state.liveStatus = live
  },

  setCallHistory(state, history) {
    state.callHistory = history
  },

  addTranscriptEntry(state, entry) {
    state.transcript.push(entry)
  },

  clearTranscript(state) {
    state.transcript = []
  },

  setLoading(state, loading) {
    state.loading = loading
  },

  setPolling(state, interval) {
    state.polling = interval
  },

  reset(state) {
    state.activeCampaign = null
    state.campaignStatus = null
    state.liveStatus = null
    state.transcript = []
    state.callHistory = []
    state.loading = false
    state.wsUrl = null
    state.wsToken = null
    if (state.polling) {
      clearInterval(state.polling)
      state.polling = null
    }
  },
}

export const actions = {
  async createCampaign({ commit }, { leadIds, settings }) {
    commit('setLoading', true)
    try {
      const { data, status } = await Api.post('/api/v1/ai-dialer/create-campaign', {
        lead_ids: leadIds,
        settings,
      }).catch(e => e)
      commit('setLoading', false)
      if (errorHandler(status, data?.message, data?.errors)) return null
      if (data.success) {
        commit('setCampaign', data.campaign)
      }
      return data
    } catch (e) {
      commit('setLoading', false)
      return null
    }
  },

  async startCampaign({ commit, state, dispatch }) {
    if (!state.activeCampaign) return
    const id = state.activeCampaign.id
    const { data } = await Api.post(`/api/v1/ai-dialer/start-campaign/${id}`).catch(e => e)
    if (data?.success) {
      dispatch('startPolling')
    }
    return data
  },

  async pauseCampaign({ state }) {
    if (!state.activeCampaign) return
    const { data } = await Api.post(`/api/v1/ai-dialer/pause-campaign/${state.activeCampaign.id}`).catch(e => e)
    return data
  },

  async resumeCampaign({ state }) {
    if (!state.activeCampaign) return
    const { data } = await Api.post(`/api/v1/ai-dialer/resume-campaign/${state.activeCampaign.id}`).catch(e => e)
    return data
  },

  async cancelCampaign({ state, commit, dispatch }) {
    if (!state.activeCampaign) return
    const { data } = await Api.post(`/api/v1/ai-dialer/cancel-campaign/${state.activeCampaign.id}`).catch(e => e)
    if (data?.success) {
      dispatch('stopPolling')
      await dispatch('fetchStatus', { force: true })
    }
    return data
  },

  async takeover({ state }) {
    if (!state.activeCampaign) return
    const { data } = await Api.post(`/api/v1/ai-dialer/takeover/${state.activeCampaign.id}`).catch(e => e)
    return data
  },

  async saveCallLog({ state, commit }, { campaignLeadId, outcome }) {
    if (!state.activeCampaign || !campaignLeadId || !state.transcript.length) return
    const { data } = await Api.post('/api/v1/ai-dialer/save-call-log', {
      campaign_lead_id: campaignLeadId,
      call_log: state.transcript,
      outcome: outcome || null,
    }).catch(e => e)
    if (data?.success) {
      commit('clearTranscript')
    }
    return data
  },

  async fetchStatus({ commit, state, force = false }) {

    if (force || state.activeCampaign?.id){
      const { data } = await Api.get(`/api/v1/ai-dialer/campaign-status/${state.activeCampaign.id}`).catch(e => e)
      if (data?.success) {
        commit('setCampaignStatus', { campaign: data.campaign, live: data.live })
        commit('setTranscriptWsCredentials', { url: data.ws_url, token: data.ws_token })
        if (data.live?.callHistory) {
        commit('setCallHistory', data.live.callHistory)
        }
        return data;
      }
    }
  },

  async fetchActivityLog({ commit, state }) {
    const { data } = await Api.get(`/api/v1/ai-dialer/activity-log/`).catch(e => e)
    return data
  },

  async recoverActiveCampaign({ commit, dispatch, state }) {
    const { data } = await Api.get(`/api/v1/ai-dialer/activity-log/`).catch(e => e)
    if (!data?.success || !Array.isArray(data.campaigns)) return null
    const active = data.campaigns.find(c => ['calling', 'paused'].includes(c.status))
    if (!active) return null

    // Set the campaign so fetchStatus can target it, then sync — campaignStatus on the
    // backend will mark the campaign cancelled if the WS server returns a 404.
    commit('setCampaign', { id: active.id, title: active.title, status: active.status })
    await dispatch('fetchStatus')

    const syncedStatus = state.campaignStatus?.campaign?.status
    const liveStatus = state.liveStatus

    // If the DB still says calling/paused but the WS server didn't return live data
    // (crashed, mid-deploy, network unreachable), force-complete the campaign so the
    // user isn't permanently blocked from creating new ones.
    if (['calling', 'paused'].includes(syncedStatus) && !liveStatus) {
      await Api.post(`/api/v1/ai-dialer/complete-campaign/${active.id}`).catch(() => {})
      commit('reset')
      return null
    }

    if (syncedStatus !== 'calling' && syncedStatus !== 'paused') {
      commit('reset')
      return null
    }
    return active.id
  },

  async fetchActivityLogLeads({ commit }, campaignId) {
    const { data } = await Api.get(`/api/v1/ai-dialer/activity-log/${campaignId}`).catch(e => e)
    return data
  },

  async cloneCampaign({ commit }, campaignId) {
    const { data } = await Api.post(`/api/v1/ai-dialer/clone-campaign/${campaignId}`).catch(e => e)
    return data
  },

  async getDialerSettings() {
    const { data } = await Api.get('/api/v1/ai-dialer/get-dialer-settings').catch(e => e)
    return data
  },

  async saveDialerSettings(_, payload) {
    const { data } = await Api.post('/api/v1/ai-dialer/save-dialer-settings', payload).catch(e => e)
    return data
  },

  async deleteDialerSettings(_, id) {
    const { data } = await Api.post(`/api/v1/ai-dialer/delete-dialer-settings/${id}`).catch(e => e)
    return data
  },

  async getDialerTerms() {
    const { data } = await Api.get('/api/v1/ai-dialer/get-dialer-terms').catch(e => e)
    return data
  },

  async getVoices() {
    const { data } = await Api.get('/api/v1/ai-dialer/get-voices').catch(e => e)
    return data
  },

  async logAcceptedTerms(_, id) {
    const { data } = await Api.post(`/api/v1/ai-dialer/log-accepted-terms/${id}`).catch(e => e)
    return data
  },

  startPolling({ commit, dispatch, state }) {
    dispatch('stopPolling')
    const interval = setInterval(() => {
      dispatch('fetchStatus')
    }, 3000)
    commit('setPolling', interval)
  },

  stopPolling({ commit, state }) {
    if (state.polling) {
      clearInterval(state.polling)
      commit('setPolling', null)
    }
  },
}
